<template>
  <div class="miu-root">
    <!-- Dropzone -->
    <FileDropZone
      :multiple="multiple"
      :accept="accept"
      :allow-folders="allowFolders"
      :disabled="disabled"
      :title="title"
      :hint="computedHint"
      @files="handleFiles"
    />

    <!-- Rejected files feedback -->
    <div v-if="rejected.length > 0" class="miu-rejected">
      <div v-for="item in visibleRejected" :key="item.id" class="miu-rejected-item">
        <i class="bi bi-exclamation-triangle-fill"></i>
        <span>{{ item.name }}: {{ item.reason }}</span>
        <button type="button" class="miu-rejected-dismiss" @click="dismissRejected(item.id)" aria-label="Dismiss">
          <i class="bi bi-x"></i>
        </button>
      </div>
      <div v-if="hiddenRejectedCount > 0" class="miu-rejected-more">
        <span>and {{ hiddenRejectedCount }} more not added</span>
        <button type="button" class="miu-btn miu-btn-link" @click="dismissAllRejected">Dismiss all</button>
      </div>
    </div>

    <!-- Queue -->
    <div v-if="queue.length > 0" class="miu-queue">
      <div class="miu-queue-header">
        <span>{{ summaryText }}</span>
        <div class="miu-queue-actions">
          <button
            v-if="!autoUpload && hasPending"
            type="button"
            class="miu-btn miu-btn-primary"
            @click="uploadPending"
          >
            Upload All
          </button>
          <button
            v-if="hasFailed"
            type="button"
            class="miu-btn miu-btn-secondary"
            @click="retryFailed"
          >
            Retry Failed
          </button>
          <button
            v-if="hasCompleted"
            type="button"
            class="miu-btn miu-btn-link"
            @click="clearCompleted"
          >
            Clear Completed
          </button>
        </div>
      </div>

      <div
        v-if="showOverallProgress"
        class="miu-progress miu-progress--overall"
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="overallPercent"
      >
        <div class="miu-progress-bar" :style="{ width: overallPercent + '%' }"></div>
      </div>

      <div class="miu-queue-list">
        <div v-for="item in queue" :key="item.id" class="miu-queue-item" :class="`miu-status-${item.status}`">
          <div class="miu-thumb">
            <img v-if="item.previewUrl" :src="item.previewUrl" :alt="item.name" loading="lazy" decoding="async">
            <i v-else class="bi bi-file-earmark"></i>
          </div>
          <div class="miu-item-info">
            <span class="miu-item-name">{{ item.name }}</span>
            <span class="miu-item-size">{{ formatFileSize(item.size) }}</span>
            <div
              v-if="item.status === 'uploading' || item.status === 'success'"
              class="miu-progress"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-valuenow="itemPercent(item)"
            >
              <div
                class="miu-progress-bar"
                :class="{
                  'miu-progress-bar--indeterminate': item.status === 'uploading' && item.progress === null,
                  'miu-progress-bar--success': item.status === 'success'
                }"
                :style="{ width: itemPercent(item) + '%' }"
              ></div>
            </div>
            <span v-if="item.status === 'error'" class="miu-item-error">{{ item.error }}</span>
            <slot name="item-extra" :item="item" :disabled="item.status === 'uploading'"></slot>
          </div>
          <div class="miu-item-status">
            <span v-if="item.status === 'queued'" class="miu-badge miu-badge-queued">Queued</span>
            <span v-else-if="item.status === 'uploading'" class="miu-badge miu-badge-uploading">
              <span class="miu-spinner"></span>
              <template v-if="item.progress === 100">Processing</template>
              <template v-else-if="item.progress !== null">{{ item.progress }}%</template>
              <template v-else>Uploading</template>
            </span>
            <span v-else-if="item.status === 'success'" class="miu-badge miu-badge-success">
              <i class="bi bi-check-circle-fill"></i> Uploaded
            </span>
            <span v-else-if="item.status === 'error'" class="miu-badge miu-badge-error">
              <i class="bi bi-x-circle-fill"></i> Failed
            </span>
          </div>
          <button
            v-if="item.status !== 'uploading'"
            type="button"
            class="miu-item-remove"
            @click="removeItem(item.id)"
            aria-label="Remove"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FileDropZone from '@/components/common/FileDropZone.vue'

const MAX_VISIBLE_REJECTED = 5

export default {
  name: 'MultiImageUploader',

  components: { FileDropZone },

  props: {
    // Mime types accepted. Empty array disables the type check.
    accept: {
      type: Array,
      default: () => ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    },
    // Max size per file, in MB.
    maxFileSizeMB: {
      type: Number,
      default: 50
    },
    // Max number of files allowed in the queue at once. 0 means no limit:
    // every file is queued and uploaded through the `concurrency` pool.
    maxFiles: {
      type: Number,
      default: 0
    },
    // Whether the dropzone/input accepts more than one file at a time.
    multiple: {
      type: Boolean,
      default: true
    },
    // Offer a "Select folder" button (folders can always be dropped).
    allowFolders: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Start uploading each file as soon as it's queued (concurrently), rather
    // than waiting for an explicit "Upload All" click.
    autoUpload: {
      type: Boolean,
      default: true
    },
    // Max number of uploads in flight at the same time, across all batches.
    // Any further files wait in the queue for a free slot.
    concurrency: {
      type: Number,
      default: 6
    },
    // Required: async (file, item, onProgress) => response. Should throw/reject
    // on failure. `item` is the queue entry (id/name/size/status/etc.) and may
    // carry extra fields a host app attached via `itemDefaults` and let the
    // uploader edit through the `item-extra` slot (e.g. a per-file title).
    // `onProgress(percent)` is optional: call it with 0-100 to drive the
    // file's progress bar; without it the bar shows as indeterminate.
    uploadHandler: {
      type: Function,
      required: true
    },
    // Optional (file) => object, merged onto each queue item when it's
    // added. Use together with the `item-extra` slot to collect extra
    // per-file input (e.g. a custom title) before/while it uploads.
    itemDefaults: {
      type: Function,
      default: () => ({})
    },
    title: {
      type: String,
      default: 'Drag files here or click to browse'
    },
    // Optional override for the hint line under the title. When omitted, a
    // hint is generated from accept / maxFileSizeMB.
    hint: {
      type: String,
      default: ''
    }
  },

  emits: ['file-uploaded', 'file-failed', 'file-rejected', 'batch-complete', 'queue-updated'],

  data() {
    return {
      queue: [],
      rejected: [],
      nextId: 1
    }
  },

  computed: {
    computedHint() {
      if (this.hint) return this.hint

      const typeLabels = this.accept
        .map(mime => mime.split('/')[1])
        .filter(Boolean)
        .map(ext => ext.toUpperCase())
        .join(', ')

      const parts = []
      if (typeLabels) parts.push(`Supports ${typeLabels}`)
      parts.push(`Max ${this.maxFileSizeMB}MB each`)
      if (this.maxFiles) parts.push(`up to ${this.maxFiles} files`)
      return parts.join(' • ')
    },

    visibleRejected() {
      return this.rejected.slice(0, MAX_VISIBLE_REJECTED)
    },

    hiddenRejectedCount() {
      return Math.max(0, this.rejected.length - MAX_VISIBLE_REJECTED)
    },

    hasPending() {
      return this.queue.some(item => item.status === 'queued' && !item.scheduled)
    },

    hasFailed() {
      return this.queue.some(item => item.status === 'error')
    },

    hasCompleted() {
      return this.queue.some(item => item.status === 'success')
    },

    summaryText() {
      const total = this.queue.length
      const done = this.queue.filter(item => item.status === 'success').length
      const uploading = this.queue.filter(item => item.status === 'uploading').length
      const failed = this.queue.filter(item => item.status === 'error').length

      let text = `${done} of ${total} file${total !== 1 ? 's' : ''} uploaded`
      if (uploading > 0) text += ` · ${uploading} uploading`
      if (failed > 0) text += ` · ${failed} failed`
      return text
    },

    // Overall bar: only worth showing while a batch is running.
    showOverallProgress() {
      return this.queue.length > 1 && this.queue.some(item => item.scheduled)
    },

    overallPercent() {
      if (this.queue.length === 0) return 0
      const total = this.queue.reduce((sum, item) => sum + this.itemPercent(item), 0)
      return Math.round(total / this.queue.length)
    }
  },

  created() {
    // Scheduler state, shared by every batch so `concurrency` holds even when
    // files are added while an earlier batch is still uploading. Kept off
    // `data` because it never needs to trigger a re-render.
    this.activeUploads = 0
    this.uploadWaiters = []
    this.isUnmounted = false
  },

  beforeUnmount() {
    this.isUnmounted = true
    this.queue.forEach(item => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
    })
    // Wake files still waiting for a slot so they can bail out.
    this.uploadWaiters.splice(0).forEach(resume => resume())
  },

  methods: {
    handleFiles(files, meta = {}) {
      // Folders usually hold stray files (Thumbs.db, .DS_Store, ...): skip those
      // with one summary line instead of one rejection per file.
      this.addFiles(files, { skipUnsupported: !!meta.fromFolder })
    },

    isAcceptedType(file) {
      return this.accept.length === 0 || this.accept.includes(file.type)
    },

    validateFile(file) {
      if (!(file instanceof File)) {
        return { valid: false, reason: 'Invalid file' }
      }

      if (!this.isAcceptedType(file)) {
        return { valid: false, reason: `Unsupported file type (${file.type || 'unknown'})` }
      }

      const maxBytes = this.maxFileSizeMB * 1024 * 1024
      if (file.size > maxBytes) {
        return { valid: false, reason: `File exceeds ${this.maxFileSizeMB}MB limit` }
      }

      return { valid: true }
    },

    fileKey(file) {
      return `${file.webkitRelativePath || ''}|${file.name}|${file.size}|${file.lastModified}`
    },

    addFiles(files, { skipUnsupported = false } = {}) {
      if (!files || files.length === 0) return

      if (!this.multiple) {
        files = files.slice(0, 1)
      }

      if (skipUnsupported) {
        const supported = files.filter(file => this.isAcceptedType(file))
        const skipped = files.length - supported.length
        if (skipped > 0) {
          this.rejected.push({
            id: this.nextId++,
            name: 'Folder',
            reason: `${skipped} unsupported file${skipped !== 1 ? 's' : ''} skipped`
          })
        }
        files = supported
      }

      const known = new Set(this.queue.map(item => this.fileKey(item.file)))
      const room = this.maxFiles ? Math.max(0, this.maxFiles - this.queue.length) : files.length
      const accepted = []

      files.forEach((file, index) => {
        if (this.maxFiles && index >= room) {
          this.rejected.push({ id: this.nextId++, name: file.name, reason: `Maximum ${this.maxFiles} files allowed` })
          return
        }

        if (known.has(this.fileKey(file))) {
          this.rejected.push({ id: this.nextId++, name: file.name, reason: 'Already in the queue' })
          return
        }

        const validation = this.validateFile(file)
        if (!validation.valid) {
          this.rejected.push({ id: this.nextId++, name: file.name, reason: validation.reason })
          this.$emit('file-rejected', file, validation.reason)
          return
        }

        known.add(this.fileKey(file))
        this.queue.push({
          id: this.nextId++,
          file,
          name: file.name,
          size: file.size,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
          status: 'queued',
          error: null,
          progress: null,
          scheduled: false,
          removed: false,
          ...this.itemDefaults(file)
        })
        // Read it back from the reactive queue: updating the raw object would
        // not re-render the row's status and progress.
        accepted.push(this.queue[this.queue.length - 1])
      })

      this.$emit('queue-updated', this.queue)

      if (accepted.length > 0 && this.autoUpload) {
        this.uploadItems(accepted)
      }
    },

    uploadPending() {
      const pending = this.queue.filter(item => item.status === 'queued' && !item.scheduled)
      this.uploadItems(pending)
    },

    retryFailed() {
      const failed = this.queue.filter(item => item.status === 'error')
      failed.forEach(item => {
        item.status = 'queued'
        item.error = null
        item.progress = null
        item.scheduled = false
      })
      this.uploadItems(failed)
    },

    async uploadItems(items) {
      if (!items || items.length === 0) return

      items.forEach(item => {
        item.scheduled = true
      })

      const results = []
      await Promise.all(items.map(item => this.uploadItem(item, results)))

      if (this.isUnmounted) return

      this.$emit('batch-complete', {
        succeeded: results.filter(r => r.status === 'success'),
        failed: results.filter(r => r.status === 'error')
      })
      this.$emit('queue-updated', this.queue)
    },

    async uploadItem(item, results) {
      await this.acquireSlot()

      // Removed from the queue, or the uploader closed, while waiting for a slot.
      if (item.removed || this.isUnmounted) {
        item.scheduled = false
        this.releaseSlot()
        return
      }

      item.status = 'uploading'
      item.progress = null

      try {
        const response = await this.uploadHandler(item.file, item, percent => this.reportProgress(item, percent))
        item.status = 'success'
        item.progress = 100
        results.push({ file: item.file, status: 'success', response })
        this.$emit('file-uploaded', item.file, response)
      } catch (error) {
        item.status = 'error'
        item.error = error?.message || 'Upload failed'
        results.push({ file: item.file, status: 'error', error: item.error })
        this.$emit('file-failed', item.file, item.error)
      } finally {
        item.scheduled = false
        this.releaseSlot()
      }
    },

    reportProgress(item, percent) {
      if (item.status !== 'uploading' || typeof percent !== 'number') return
      item.progress = Math.max(0, Math.min(100, Math.round(percent)))
    },

    acquireSlot() {
      if (this.activeUploads < this.concurrency) {
        this.activeUploads++
        return Promise.resolve()
      }
      return new Promise(resolve => this.uploadWaiters.push(resolve))
    },

    releaseSlot() {
      const next = this.uploadWaiters.shift()
      if (next) {
        // Hand the slot straight to the next file in line.
        next()
      } else {
        this.activeUploads--
      }
    },

    itemPercent(item) {
      if (item.status === 'success') return 100
      if (item.status === 'uploading') return item.progress ?? 0
      return 0
    },

    removeItem(id) {
      const index = this.queue.findIndex(item => item.id === id)
      if (index === -1) return
      const [item] = this.queue.splice(index, 1)
      item.removed = true
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl)
      this.$emit('queue-updated', this.queue)
    },

    dismissRejected(id) {
      this.rejected = this.rejected.filter(item => item.id !== id)
    },

    dismissAllRejected() {
      this.rejected = []
    },

    clearCompleted() {
      const remaining = []
      this.queue.forEach(item => {
        if (item.status === 'success') {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
        } else {
          remaining.push(item)
        }
      })
      this.queue = remaining
      this.$emit('queue-updated', this.queue)
    },

    reset() {
      this.queue.forEach(item => {
        item.removed = true
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      })
      this.queue = []
      this.rejected = []
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
    }
  }
}
</script>

<style scoped>
.miu-root {
  width: 100%;
}

.miu-rejected {
  margin-bottom: 1rem;
}

.miu-rejected-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
}

.miu-rejected-item span {
  flex: 1;
}

.miu-rejected-dismiss {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0.125rem;
}

.miu-rejected-more {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #b91c1c;
}

.miu-queue-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #374151;
  font-weight: 500;
}

.miu-queue-actions {
  display: flex;
  gap: 0.5rem;
}

.miu-btn {
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.miu-btn-primary {
  background: #0ea5e9;
  color: white;
}

.miu-btn-primary:hover {
  background: #0284c7;
}

.miu-btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.miu-btn-secondary:hover {
  background: #e5e7eb;
}

.miu-btn-link {
  background: none;
  color: #6b7280;
  text-decoration: underline;
  padding: 0.4rem 0.25rem;
}

.miu-queue-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 360px;
  overflow-y: auto;
}

.miu-queue-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
}

.miu-queue-item.miu-status-success {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.miu-queue-item.miu-status-error {
  border-color: #fecaca;
  background: #fef2f2;
}

.miu-thumb {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #9ca3af;
  font-size: 1.25rem;
}

.miu-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.miu-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.miu-item-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.miu-item-size {
  font-size: 0.75rem;
  color: #6b7280;
}

.miu-item-error {
  font-size: 0.75rem;
  color: #dc2626;
}

.miu-progress {
  height: 4px;
  margin-top: 0.35rem;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}

.miu-progress--overall {
  height: 6px;
  margin: 0 0 1rem 0;
}

.miu-progress-bar {
  height: 100%;
  border-radius: inherit;
  background: #0ea5e9;
  transition: width 0.2s ease;
}

.miu-progress-bar--success {
  background: #22c55e;
}

.miu-progress-bar--indeterminate {
  width: 40% !important;
  animation: miu-indeterminate 1.1s ease-in-out infinite;
}

@keyframes miu-indeterminate {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}

.miu-item-status {
  flex-shrink: 0;
}

.miu-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
}

.miu-badge-queued {
  background: #f3f4f6;
  color: #6b7280;
}

.miu-badge-uploading {
  background: #e0f2fe;
  color: #0369a1;
}

.miu-badge-success {
  background: #dcfce7;
  color: #166534;
}

.miu-badge-error {
  background: #fee2e2;
  color: #b91c1c;
}

.miu-spinner {
  width: 10px;
  height: 10px;
  border: 2px solid rgba(3, 105, 161, 0.3);
  border-top-color: #0369a1;
  border-radius: 50%;
  display: inline-block;
  animation: miu-spin 0.8s linear infinite;
}

@keyframes miu-spin {
  to { transform: rotate(360deg); }
}

.miu-item-remove {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
  flex-shrink: 0;
}

.miu-item-remove:hover {
  color: #dc2626;
}
</style>
